import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ScanLine, Upload, Loader2, ArrowUpRight } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { createExpense, listCategories, listPrograms, ocrReceipt } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { EXPENSE } from "@/constants/testIds";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function NewExpense() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [receiptData, setReceiptData] = useState(null);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confidence, setConfidence] = useState(null);
  const fileRef = useRef(null);

  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [spentOn, setSpentOn] = useState(new Date().toISOString().slice(0, 10));
  const [programId, setProgramId] = useState("");
  const [category, setCategory] = useState("Other");
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([listPrograms(), listCategories()]);
      setPrograms(p);
      setCategories(c);
      if (p[0]) setProgramId(p[0].id);
    })();
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast({ title: "File too large", description: "Please use an image under 8 MB.", variant: "destructive" });
      return;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    setReceiptData(dataUrl);
    runOcr(dataUrl);
  };

  const runOcr = async (dataUrl) => {
    setOcrRunning(true);
    try {
      const res = await ocrReceipt(dataUrl);
      if (res.vendor) setVendor(res.vendor);
      if (res.total_amount) setAmount(String(res.total_amount));
      if (res.currency) setCurrency(res.currency);
      if (res.date_iso) setSpentOn(res.date_iso);
      if (res.category_guess && categories.some((c) => c.name === res.category_guess)) {
        setCategory(res.category_guess);
      }
      if (res.line_items?.length) setLineItems(res.line_items);
      setConfidence(res.confidence ?? null);
      toast({ title: "Receipt parsed", description: `Confidence ${(Math.round((res.confidence || 0) * 100))}%` });
    } catch (err) {
      toast({ title: "OCR failed", description: err?.response?.data?.detail || "Try a clearer image.", variant: "destructive" });
    } finally {
      setOcrRunning(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!programId) {
      toast({ title: "Pick a program first", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await createExpense({
        program_id: programId,
        category,
        vendor,
        amount: parseFloat(amount),
        currency: currency.toUpperCase(),
        spent_on: spentOn,
        notes: notes || null,
        receipt_data_url: receiptData,
        line_items: lineItems,
      });
      toast({ title: "Expense submitted", description: "It's now awaiting approval." });
      navigate("/app/expenses");
    } catch (err) {
      toast({ title: "Could not submit", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader title="Submit an expense" subtitle="Photo of the receipt is all we need — AI will fill in the rest." />
      <PageBody>
        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8">
          {/* Receipt panel */}
          <div className="lg:col-span-5">
            <div className="border border-border bg-card rounded-md overflow-hidden">
              {receiptData ? (
                <img src={receiptData} alt="Receipt" className="w-full max-h-[420px] object-contain bg-muted" />
              ) : (
                <div className="aspect-[3/4] flex flex-col items-center justify-center bg-muted/40 text-center px-6">
                  <Camera size={40} strokeWidth={1.2} className="text-muted-foreground" />
                  <div className="serif text-xl mt-4 text-primary">Add a receipt</div>
                  <p className="text-xs text-muted-foreground mt-2 max-w-xs">JPG or PNG, up to 8 MB. We&apos;ll extract vendor, amount, and date automatically.</p>
                </div>
              )}
              <div className="p-4 border-t border-border flex gap-2">
                <input
                  ref={fileRef}
                  data-testid={EXPENSE.receiptFile}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-background hover:bg-accent px-3 py-2 text-sm"
                >
                  <Upload size={14} /> {receiptData ? "Replace" : "Upload receipt"}
                </button>
                {receiptData && (
                  <button
                    type="button"
                    data-testid={EXPENSE.runOcr}
                    onClick={() => runOcr(receiptData)}
                    disabled={ocrRunning}
                    className="inline-flex items-center justify-center gap-1.5 rounded-md bg-secondary text-secondary-foreground px-3 py-2 text-sm font-medium hover:bg-secondary/90 disabled:opacity-60"
                  >
                    {ocrRunning ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />}
                    {ocrRunning ? "Scanning…" : "Re-scan"}
                  </button>
                )}
              </div>
              {confidence !== null && (
                <div data-testid={EXPENSE.ocrConfidence} className="px-4 pb-4 text-[11px] text-muted-foreground uppercase tracking-wider">
                  OCR confidence: <span className="mono text-primary">{Math.round(confidence * 100)}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 space-y-5">
            <Row>
              <Field label="Vendor">
                <input data-testid={EXPENSE.vendorInput} required value={vendor} onChange={(e) => setVendor(e.target.value)} className="td-input" placeholder="Costco, Lyft, Marriott…" />
              </Field>
              <Field label="Date">
                <input data-testid={EXPENSE.dateInput} required type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} className="td-input" />
              </Field>
            </Row>
            <Row>
              <Field label="Amount">
                <input data-testid={EXPENSE.amountInput} required type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="td-input mono" placeholder="0.00" />
              </Field>
              <Field label="Currency">
                <input data-testid={EXPENSE.currencyInput} required maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className="td-input mono" />
              </Field>
            </Row>
            <Row>
              <Field label="Program">
                <Select value={programId} onValueChange={setProgramId}>
                  <SelectTrigger data-testid={EXPENSE.programSelect} className="td-input h-auto py-2.5">
                    <SelectValue placeholder="Choose program" />
                  </SelectTrigger>
                  <SelectContent>
                    {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Category">
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid={EXPENSE.categorySelect} className="td-input h-auto py-2.5">
                    <SelectValue placeholder="Choose category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </Row>
            <Field label="Notes (optional)">
              <textarea data-testid={EXPENSE.notesInput} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="td-input" placeholder="Anything the approver should know?" />
            </Field>

            {lineItems.length > 0 && (
              <div className="border border-border bg-card rounded-md p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Parsed line items</div>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {lineItems.map((li, i) => (
                    <li key={i} className="flex items-center justify-between gap-3">
                      <span className="truncate">{li.description || "—"}</span>
                      <span className="mono text-muted-foreground tabular shrink-0">
                        {li.amount != null ? `$${Number(li.amount).toFixed(2)}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4">
              <button type="button" onClick={() => navigate("/app/expenses")} className="rounded-md border border-border bg-background hover:bg-accent px-4 py-2.5 text-sm">
                Cancel
              </button>
              <button
                type="submit"
                data-testid={EXPENSE.submit}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
                {submitting ? "Submitting…" : "Submit for approval"}
              </button>
            </div>
          </div>
        </form>
      </PageBody>
      <style>{`
        .td-input { width: 100%; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 6px; padding: 10px 12px; font-size: 14px; color: hsl(var(--foreground)); }
        .td-input:focus { outline: none; border-color: hsl(var(--ring)); box-shadow: 0 0 0 3px hsl(var(--ring) / 0.12); }
      `}</style>
    </>
  );
}

function Row({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
